import { useEffect, useRef } from "react";
import { useState } from "react";
import { use100vh } from "react-div-100vh";
import { XMarkIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import TextSkeleton from "../../components/SkeletonComponents/TextSkeleton";

const ExerciseNameEditor = (props) => {
  const initialExerciseName = props.exerciseName;
  const windowHeight = use100vh();
  const [exerciseName, setExerciseName] = useState(props.exerciseName);
  const [previousExerciseNames, setPreviousExerciseNames] = useState([]);
  const [suggestionsState, setSuggestionsState] = useState({
    showSuggestions: false,
    suggestions: [],
  });
  const [inputBlurred, setInputBlurred] = useState(false);
  const inputRef = useRef(null);
  const isLoading = props.isLoading;

  useEffect(() => {
    if (props.exerciseNameSuggestions) {
      setPreviousExerciseNames(props.exerciseNameSuggestions);
    }
  }, [props.exerciseNameSuggestions]);

  useEffect(() => {
    if (props.exerciseName) {
      setExerciseName(props.exerciseName);
    }
  }, [props.exerciseName]);

  useEffect(() => {
    if (inputBlurred) {
      setExerciseName(exerciseName.trim());

      if (exerciseName !== initialExerciseName) {
        props.onExerciseNameChange(exerciseName.trim());
      }

      setSuggestionsState({
        ...suggestionsState,
        showSuggestions: false,
      });
      setInputBlurred(false);
    }
  }, [exerciseName, inputBlurred]);

  if (!previousExerciseNames) return;

  const handleExerciseNameChange = (event) => {
    setExerciseName(event.target.value);
    const userInput = event.target.value;
    const filteredSuggestions = previousExerciseNames.filter((suggestion) => {
      if (suggestion)
        return suggestion.toLowerCase().indexOf(userInput.toLowerCase()) > -1;
    });

    if (userInput !== "")
      setSuggestionsState({
        suggestions: filteredSuggestions,
        showSuggestions: true,
      });
    else
      setSuggestionsState({
        suggestions: filteredSuggestions,
        showSuggestions: false,
      });
  };

  const handleExerciseNameBlur = (event) => {
    const relatedTarget = event.relatedTarget;
    if (
      relatedTarget &&
      relatedTarget.getAttribute("type") === "exercise-suggestion"
    ) {
      const suggestedExerciseName = relatedTarget.innerText;
      setExerciseName(suggestedExerciseName);
    }
    setInputBlurred(true);
  };

  const handleExerciseNameFocus = () => {
    const filteredSuggestions = previousExerciseNames.filter((suggestion) => {
      if (suggestion)
        return (
          suggestion.toLowerCase().indexOf(exerciseName.toLowerCase()) > -1
        );
    });
    if (exerciseName !== "")
      setSuggestionsState({
        suggestions: filteredSuggestions,
        showSuggestions: true,
      });
  };

  const handleInputCleared = () => {
    setExerciseName("");
    if (inputRef.current) {
      inputRef.current.focus();
    }
    setSuggestionsState({
      suggestions: previousExerciseNames,
      showSuggestions: false,
    });
  };

  return (
    <div className="relative">
      <div className="rounded-md">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            {props.label || "Exercise"}
          </label>
          {!isLoading ? (
            <div className="relative">
              {exerciseName !== "" && (
                <XMarkIcon
                  className="h-5 w-auto text-gray-400 absolute top-[19px] -translate-y-1/2 right-3"
                  aria-hidden="true"
                  onClick={handleInputCleared}
                />
              )}
              <input
                id="exercise-name"
                name="exercise-name"
                type="text"
                autoComplete="off"
                required
                tabIndex={-1}
                className={classNames(
                  "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm",
                  {
                    "pr-10": exerciseName !== "",
                  }
                )}
                placeholder={props.placeholder || ""}
                value={exerciseName}
                onChange={handleExerciseNameChange}
                onFocus={handleExerciseNameFocus}
                onBlur={handleExerciseNameBlur}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.target.blur();
                  }
                }}
                ref={inputRef}
              />
            </div>
          ) : (
            <TextSkeleton height="38px" className="w-full mt-1" />
          )}
        </div>
      </div>
      {suggestionsState.showSuggestions &&
        suggestionsState.suggestions.length > 0 && (
          <div className="absolute z-50 w-full left-0 top-[calc(100%+8px)]">
            <div
              className="flex flex-col bg-white w-full rounded-md overflow-y-scroll shadow-lg ring-1 ring-black ring-opacity-5"
              style={{
                maxHeight: `calc(${windowHeight}px - 63px - 78px - 100px - 63px - 20px)`,
              }}
            >
              <span className="text-xs text-gray-500 px-3 pb-3 pt-3">
                Suggestions based on your previous exercises
              </span>
              {suggestionsState.suggestions &&
                suggestionsState.suggestions.length !== 0 &&
                suggestionsState.suggestions.map((suggestion) => {
                  return (
                    <div
                      type="exercise-suggestion"
                      className="relative text-gray-700 text-left px-3 py-[9px] text-sm w-full hover:cursor-pointer"
                      key={suggestion}
                      tabIndex={0}
                    >
                      {suggestion}
                    </div>
                  );
                })}
            </div>
          </div>
        )}
    </div>
  );
};

export default ExerciseNameEditor;
